import './MatchEditLigue.scss';

import { Divider, Hidden, Pagination } from '@mui/material';
import classNames from 'classnames';
import { EventStatusEnum } from 'domain/event/EventStatusEnum';
import { MatchGroup } from 'domain/event/match/MatchGroup';
import {
  TEXT_BY_PHASE_ORDER,
  toMatchLigueModel,
  UnpyMatch,
  UnpyMatchFilters,
} from 'domain/event/match/UnpyMatch';
import { SystemPointsEnum } from 'domain/event/SystemPoints.enum';
import { TypeEventCodeEnum } from 'domain/event/TypeEventCodeEnum';
import { UnpyEvent } from 'domain/event/UnpyEvent';
import { isTournament } from 'domain/event/UnpyEvent.func';
import { UnpyEventView } from 'domain/event/UnpyEventView';
import {
  setMatchUpdateModalClose,
  setMatchUpdateModalOpen,
} from 'domain/modal/store/ModalSlice';
import { TranslationType } from 'domain/translation/Translation.repository';
import { EventMatchsParametersProps } from 'primary/classements/MatchsParametersByEventType';
import { Match } from 'primary/Components/bracket/CustomBracket/types';
import { MatchView } from 'primary/Components/bracket/MatchView';
import { LoadingScreen } from 'primary/Components/LoadingScreen/LoadingScreen';
import { UnpyTabs } from 'primary/Components/Tabs/UnpyTabs';
import { MatchListByGroupsMobile } from 'primary/events/components/matchs/edit/mobile/MatchListByGroupsMobile';
import { TeamScoreEditRow } from 'primary/events/components/matchs/edit/TeamScoreEditRow';
import { HeaderMatchsEdit } from 'primary/events/components/matchs/HeaderMatchsEdit';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useFetchWithFilterPage } from 'primary/hooks/useFetchWithFilterPage';
import { useTranslate } from 'primary/hooks/useTranslate';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

export const MatchEditLigue = ({ event, retry, asView }: EventMatchsParametersProps) => {
  const [mode, setMode] = React.useState<'bracket' | 'session'>('session');
  const [filters, setFilters] = React.useState<UnpyMatchFilters>({
    onlyScoreUnset: undefined,
    textValue: undefined,
    onlyUpper: true,
  });
  const { authRepository } = useContextDependency();

  const t = useTranslate();
  const setFilterByKey = (
    key: keyof UnpyMatchFilters,
    val?: string | number | boolean,
  ) => {
    setFilters({ ...filters, [key]: val });
  };

  return (
    <div className={'matchEditLigue'}>
      <Hidden smUp>
        <HeaderMatchsEdit
          withRoundFilter={false}
          retry={retry}
          event={event}
          withTextField
          withOnlyMyMatchs={
            asView && event.creator?.id !== authRepository?.currentUser?.id
          }
          withOnlyScoreUnset={!asView}
          filters={filters}
          setFilters={setFilterByKey}
        />
        <MatchListByGroupsMobile eventId={event.id} filters={filters} asView={asView} />
      </Hidden>

      <Hidden smDown>
        <HeaderMatchsEdit
          retry={retry}
          event={event}
          onChangeMode={setMode}
          mode={mode}
          filters={filters}
          withTextField
          withOnlyMyMatchs={
            asView && event.creator?.id !== authRepository?.currentUser?.id
          }
          withOnlyScoreUnset={!asView}
          setFilters={setFilterByKey}
        />
        {mode === 'session' && (
          <MatchListViewByGroup filters={filters} event={event} asView={asView} />
        )}
        {mode === 'bracket' && <BracketLigue eventId={event.id} asView={asView} />}
      </Hidden>
    </div>
  );
};

type MatchListViewByGroupProps = {
  event?: UnpyEvent | UnpyEventView;
  filters?: UnpyMatchFilters;
  asView?: boolean;
};
export const MatchListViewByGroup = ({
  event,
  filters,
  asView,
}: MatchListViewByGroupProps) => {
  const t = useTranslate();
  const { matchsRepository } = useContextDependency();
  const [selectedGroup, setSelectedGroup] = React.useState<MatchGroup | null>(null);
  const {
    setFilters,
    activeFilters,
    fetchedResource,
    nextPage,
    loading,
    goToPage,
    retry,
    addToCurrentPage,
  } = useFetchWithFilterPage<MatchGroup, UnpyMatchFilters>({
    keyStoreFilters: 'groups-ligue-filters',
    staticFilters: {
      idEvent: event?.id,
    },
    filters: {
      ...filters,
      onlyUpper: event?.typeEvent.code === TypeEventCodeEnum.TOURNAMENT_DOUBLE_ELEM,
    },
    callApi: (filters, page) => matchsRepository.searchGroup(filters, page, 10),
  });
  const tabsConf = [
    {
      label: t(`event.matchs.upper`),
      action: () => {
        setFilters({ ...activeFilters?.filters, onlyUpper: true, onlyLower: undefined });
      },
    },
    {
      label: t(`event.matchs.lower`),
      action: () => {
        setFilters({ ...activeFilters?.filters, onlyLower: true, onlyUpper: undefined });
      },
    },
  ];

  useEffect(() => {
    setFilters({ ...activeFilters?.filters, ...filters });
  }, [filters]);

  useEffect(() => {
    if (event?.typeEvent.code === TypeEventCodeEnum.TOURNAMENT_DOUBLE_ELEM) {
      setFilters({ ...activeFilters?.filters, onlyUpper: true, onlyLower: undefined });
    }
  }, []);

  useEffect(() => {
    if (!selectedGroup && fetchedResource?.content?.[0]) {
      setSelectedGroup(fetchedResource?.content?.[0]);
    }
  }, [selectedGroup, fetchedResource?.content]);
  // eslint-disable-next-line prefer-spread
  const maxGroupPhaseOrder = Math.max.apply(
    Math,
    fetchedResource?.content?.map((group) => group.phaseOrder) ?? [],
  );
  return (
    <div className={'matchListViewByGroup'}>
      <div className={'matchListViewByGroup__groupList'}>
        {event?.typeEvent.code === TypeEventCodeEnum.TOURNAMENT_DOUBLE_ELEM && (
          <UnpyTabs tabsConf={tabsConf} center />
        )}
        <div className={'matchListViewByGroup__groupList-content --scrollBarContent'}>
          <h5>Liste des groupes :</h5>
          <Divider />
          {loading && <LoadingScreen loading message={'Récupération des groupes'} />}
          {!loading && !fetchedResource?.content?.length && (
            <p>{t('event.matchs.noGroup')}</p>
          )}
          {!loading &&
            fetchedResource?.content &&
            fetchedResource?.content.map((group) => (
              <div
                key={group.id}
                className={classNames('-item', {
                  ['-selected']: group.id === selectedGroup?.id,
                })}
                onClick={() => setSelectedGroup(group)}
              >
                <p>
                  {maxGroupPhaseOrder && isTournament(event)
                    ? TEXT_BY_PHASE_ORDER[maxGroupPhaseOrder - group.phaseOrder]
                    : group.title}
                </p>
                <span>
                  {t('event.matchs.numberMatchs', {
                    count: group?.totalMatchsInGroup ?? 0,
                  })}
                </span>
              </div>
            ))}
        </div>
        <div className={'matchListViewByGroup__groupList-pagination'}>
          <Pagination
            key={fetchedResource?.currentPage}
            count={fetchedResource?.pagesCount || 1}
            page={fetchedResource?.currentPage ? fetchedResource?.currentPage + 1 : 1}
            size={'medium'}
            onChange={(e, value) => goToPage(value - 1)}
          />
        </div>
      </div>
      <div className={'matchListViewByGroup__matchList'}>
        {!selectedGroup && (
          <p>
            {t(`event.matchs.selectGroup.${event?.typeEvent?.code}` as TranslationType)}
          </p>
        )}
        {selectedGroup && (
          <MatchListOfGroup
            key={selectedGroup.id}
            group={selectedGroup}
            filters={filters}
            asView={asView}
          />
        )}
      </div>
    </div>
  );
};

type MatchListByGroupProps = {
  group: MatchGroup;
  filters?: UnpyMatchFilters;
  asView?: boolean;
  maxGroupPhaseOrder?: number;
};
const MatchListOfGroup = ({ group, filters, asView }: MatchListByGroupProps) => {
  const { matchsRepository } = useContextDependency();
  const {
    setFilters,
    activeFilters,
    fetchedResource,
    nextPage,
    loading,
    retry,
    goToPage,
    addToCurrentPage,
  } = useFetchWithFilterPage<UnpyMatch, UnpyMatchFilters>({
    keyStoreFilters: 'matchs-ligue-filters',
    staticFilters: {
      idGroup: group.id,
    },
    filters: filters ?? {},
    callApi: (filters, page) => matchsRepository.search(filters, page, 10),
  });
  const t = useTranslate();

  const matchsFormatted = fetchedResource?.content?.map(toMatchLigueModel);
  useEffect(() => {
    setFilters({ ...(activeFilters?.filters ?? {}), ...filters });
  }, [filters]);
  return (
    <div className={'matchListOfGroup'}>
      <div className={'matchListOfGroup-content --scrollBarContent'}>
        <h5>Matchs {group.title}</h5>
        <Divider />
        {!matchsFormatted && loading && (
          <LoadingScreen
            loading={loading}
            message={t('event.matchs.matchInGroupLoading')}
          />
        )}
        {!loading && !matchsFormatted?.length && (
          <p>{t('event.matchs.noMatchsInGroup')}</p>
        )}
        {!loading && !matchsFormatted?.length && (
          <p>{t('event.matchs.noMatchsInGroup')}</p>
        )}
        {matchsFormatted &&
          matchsFormatted.map((match) => (
            <TeamScoreEditRow
              asView={asView}
              isClassementReadOnly={group?.event?.status !== EventStatusEnum.ON_GOING}
              key={match.id}
              className={'-item'}
              match={match.unpyMatch}
              systemPoint={group.event.pointSystem ?? SystemPointsEnum.DEFAULT}
              onEdited={retry}
              typeEvent={group.event.typeEvent.code}
            />
          ))}
      </div>
      <div className={'matchListOfGroup-pagination'}>
        <Pagination
          key={fetchedResource?.currentPage}
          count={fetchedResource?.pagesCount || 1}
          page={fetchedResource?.currentPage ? fetchedResource?.currentPage + 1 : 1}
          size={'medium'}
          onChange={(e, value) => goToPage(value - 1)}
        />
      </div>
    </div>
  );
};

type BracketLigueProps = {
  eventId: number;
  asView?: boolean;
};
const BracketLigue = ({ eventId, asView }: BracketLigueProps) => {
  const { matchsRepository } = useContextDependency();

  const {
    setFilters,
    activeFilters,
    fetchedResource,
    nextPage,
    loading,
    retry,
    addToCurrentPage,
  } = useFetchWithFilterPage<MatchGroup, UnpyMatchFilters>({
    keyStoreFilters: 'matchs-ligue-filters',
    staticFilters: {
      idEvent: eventId,
      withMatchs: true,
    },
    filters: {},
    callApi: (filters, page) => matchsRepository.searchGroup(filters, page, 5),
  });

  const formattedGroups = fetchedResource?.content?.map((group) => ({
    ...group,
    matchs: group.matchs.map(toMatchLigueModel),
  }));
  const dispatch = useDispatch();
  const closeEditScore = () => dispatch(setMatchUpdateModalClose());
  const openEditScore = (matchToEdit: Match) => {
    dispatch(
      setMatchUpdateModalOpen({
        props: {
          //@ts-ignore
          systemPoint: formattedGroups?.[0]?.event?.pointSystem,
          handleClose: closeEditScore,
          onValidEdit: () => {
            retry();
            closeEditScore();
          },
          match: matchToEdit.unpyMatch,
        },
      }),
    );
  };

  const t = useTranslate();
  return (
    <div className={'-bracketLigue'}>
      {loading && (
        <LoadingScreen
          loading={loading}
          message={t('event.matchs.loadingMatchInGroup')}
        />
      )}
      {!loading &&
        formattedGroups?.map((group) => (
          <div key={group.id} className={'-matchGroupContainer'}>
            <div className={'header'}>
              {t('event.ligue.pouleLabel', { suffixe: group.phaseOrder })}
            </div>
            <div key={group.id} className={'-matchGroup'}>
              {group.matchs
                //@ts-ignore
                .sort((a, b) => b.id - a.id)
                ?.map((match) => (
                  <MatchView
                    onEdit={() => openEditScore(match)}
                    key={match.id}
                    event={group.event}
                    onEdited={retry}
                    match={match}
                    topParty={match.participants[0]}
                    bottomParty={match.participants[1]}
                    bottomWon={match.participants[1].isWinner ?? false}
                    topWon={match.participants[0].isWinner ?? false}
                    isEditable={
                      group?.event?.status === EventStatusEnum.ON_GOING && !asView
                    }
                    onMatchClick={() => console.log('CLICKED')}
                    onPartyClick={() => console.log('PARTY CLICKED')}
                    onMouseEnter={() => console.log('MOUSE ENTER')}
                    onMouseLeave={() => console.log('MOUSE LEAVE')}
                    topHovered={false}
                    bottomHovered={false}
                    bottomText={''}
                    topText={''}
                    // @ts-ignore
                    resultFallback={(p: any) => 'ok'}
                    teamNameFallback={'ok'}
                  />
                ))}
            </div>
          </div>
        ))}
    </div>
  );
};
